import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon, Checkbox } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCCompareAppointmentSelfAndStaffProps = {
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}
const CardMCCompareAppointmentSelfAndStaff = (props: CardMCCompareAppointmentSelfAndStaffProps) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  const [viewMode, setViewMode] = useState<'overall' | 'age'>('overall');
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  const [isMonthCombined, setIsMonthCombined] = useState(false);
  const [isTransposed, setIsTransposed] = useState(false);

  const [rawData, setRawData] = useState<any[]>([]);
  const [processedOverallData, setProcessedOverallData] = useState<any[]>([]);
  const [ageData, setAgeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch logic
  const fetchData = async () => {
    setLoading(true);
    try {
      // Construct date range
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const startDate = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
      const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;

      let res = null
      let data = null
      if (props.MeChartSequence?.sequenceIndex === "Action") {
        res = await props.runSequence({
          sequence: "MeChart", action: "QuerySQL",
          chart: "appointment-staff-vs-self",
          startDate: startDate,
          endDate: endDate
        })
        
        console.log('res: ', res);
        if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          data = res?.[0]?.items;
        }

        if (Array.isArray(data)) {
          setRawData(data);
          processAgeData(data);
        }
      }

    const response = await fetch(`http://localhost:3002/api/appointment-self-and-staff?startDate=${startDate}&endDate=${endDate}`);
    const sqlData = await response.json();
    console.log('sqlData: ', sqlData);

    } catch (error) {
      console.error("Failed to fetch appointment data", error);
    } finally {
      setLoading(false);
    }
  };

  const processAgeData = (data: any[]) => {
    const ageAgg: Record<string, { self: number, staff: number }> = {
      '0-18': { self: 0, staff: 0 },
      '19-24': { self: 0, staff: 0 },
      '25-34': { self: 0, staff: 0 },
      '35-44': { self: 0, staff: 0 },
      '45+': { self: 0, staff: 0 }
    };
    const ageOrder = ['0-18', '19-24', '25-34', '35-44', '45+'];

    data.forEach((row: any) => {
      const source = row.source;
      const age = parseFloat(row.patient_age);
      let ageGroup = '';

      if (age <= 18) ageGroup = '0-18';
      else if (age >= 19 && age <= 24) ageGroup = '19-24';
      else if (age >= 25 && age <= 34) ageGroup = '25-34';
      else if (age >= 35 && age <= 44) ageGroup = '35-44';
      else if (age >= 45) ageGroup = '45+';

      if (ageGroup && ageAgg[ageGroup]) {
        if (source === 'Self-Booking') ageAgg[ageGroup].self++;
        else if (source === 'Staff-Booking') ageAgg[ageGroup].staff++;
      }
    });

    const finalAgeData: any[] = [];
    ageOrder.forEach((group, index) => {
      finalAgeData.push({ age: group, source: 'Self-Booking', value: ageAgg[group].self, color: '#4285F4', order: index });
      finalAgeData.push({ age: group, source: 'Staff-Booking', value: ageAgg[group].staff, color: '#EA4335', order: index });
    });
    setAgeData(finalAgeData);
  };

  // Re-process overall data when rawData or toggle changes
  useEffect(() => {
    if (!rawData.length) {
      setProcessedOverallData([]);
      return;
    }

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    const thaiMonthsShort = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ];

    if (isMonthCombined) {
      // Aggregate by Month Index (1-12)
      const agg: Record<number, { self: number, staff: number }> = {};
      for (let i = 1; i <= 12; i++) agg[i] = { self: 0, staff: 0 };

      rawData.forEach((row: any) => {
        const mIndex = parseInt(row.month_index);
        const source = row.source;
        if (!isNaN(mIndex) && agg[mIndex]) {
          if (source === 'Self-Booking') agg[mIndex].self++;
          else if (source === 'Staff-Booking') agg[mIndex].staff++;
        }
      });

      const finalData: any[] = [];
      for (let i = 1; i <= 12; i++) {
        finalData.push({ month: thaiMonths[i - 1], source: 'Self-Booking', value: agg[i].self, color: '#4285F4', order: i });
        finalData.push({ month: thaiMonths[i - 1], source: 'Staff-Booking', value: agg[i].staff, color: '#EA4335', order: i });
      }
      setProcessedOverallData(finalData);

    } else {
      // Aggregate by Year-Month
      const agg: Record<string, { self: number, staff: number, year: number, month: number }> = {};

      rawData.forEach((row: any) => {
        const y = parseInt(row.year_index);
        const m = parseInt(row.month_index);
        const source = row.source;
        const key = `${y}-${String(m).padStart(2, '0')}`;

        if (!agg[key]) agg[key] = { self: 0, staff: 0, year: y, month: m };
        if (source === 'Self-Booking') agg[key].self++;
        else if (source === 'Staff-Booking') agg[key].staff++;
      });

      // Fill ALL month-year slots from startDate to endDate so every slot renders (even when count = 0)
      const finalData: any[] = [];
      let order = 0;
      let cur = new Date(year, month - 1, 1);      // start: first day of start month
      const end = new Date(endYear, endMonth - 1, 1); // end: first day of end month

      while (cur <= end) {
        const y = cur.getFullYear();
        const m = cur.getMonth() + 1;
        const key = `${y}-${String(m).padStart(2, '0')}`;
        const item = agg[key] || { self: 0, staff: 0, year: y, month: m };

        const label = `${thaiMonthsShort[m - 1]} ${y % 100}`;

        finalData.push({ month: label, source: 'Self-Booking', value: item.self, color: '#4285F4', order });
        finalData.push({ month: label, source: 'Staff-Booking', value: item.staff, color: '#EA4335', order });
        order++;

        cur = new Date(y, m, 1); // advance one month
      }

      setProcessedOverallData(finalData);
    }

  }, [rawData, isMonthCombined]);


  const onSearch = () => {
    fetchData();
  };
  const onPrint = () => { window.print(); };
  const onDownload = () => {
    if (!processedOverallData.length && !ageData.length) {
      alert('No data to download');
      return;
    }

    const dataToExport = (viewMode === 'overall' ? processedOverallData : ageData).map(item => ({ ...item }));

    // Simple export logic, same as existing
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AppointmentSelfVsStaff");
    XLSX.writeFile(wb, `Appointment_Self_Vs_Staff_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Initial Fetch and re-fetch when date range changes
  useEffect(() => {
    fetchData();
  }, []);

  // 1. Overall
  const getVegaOverallData = () => {
    return processedOverallData;
  };

  const getVegaOverallLineData = () => {
    return processedOverallData;
  };

  const getVegaAgeData = () => {
    return ageData;
  };

  const getVegaAgeLineData = () => {
    return ageData;
  };

  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    const isOverall = viewMode === 'overall';
    const isBar = chartType === 'bar';

    let data;
    if (isOverall) {
      data = isBar ? getVegaOverallData() : getVegaOverallLineData();
    } else {
      data = isBar ? getVegaAgeData() : getVegaAgeLineData();
    }

    const xField = isOverall ? 'month' : 'age';
    // If Separate View, title is "Month-Year", else "Month"
    const xTitle = isOverall ? (isMonthCombined ? 'Month' : 'Month-Year') : 'Age Group';

    // Define Legend Title based on data source content
    // All views now use Self/Staff Booking
    const legendTitle = "Booking Channel";

    if (isBar) {
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "bar", tooltip: true },
        encoding: {
          [isTransposed ? 'y' : 'x']: { field: xField, type: "ordinal", axis: { title: xTitle, labelAngle: (isTransposed || isMonthCombined) ? 0 : -45 }, sort: { field: "order" } },
          [isTransposed ? 'x' : 'y']: { field: "value", type: "quantitative", axis: { title: "Count" } },
          xOffset: isTransposed ? undefined : { field: "source" },
          yOffset: isTransposed ? { field: "source" } : undefined,
          color: {
            field: "source",
            type: "nominal",
            scale: { range: ['#4285F4', '#EA4335'] },
          },
          tooltip: [
            { field: xField, title: xTitle },
            { field: "source", title: legendTitle },
            { field: "value", title: "Count", format: "," }
          ]
        }
      };
    } else {
      // Line Chart — same colors as Bar so toggling doesn't confuse the user
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "line", point: true, tooltip: true },
        encoding: {
          x: { field: xField, type: "ordinal", axis: { title: xTitle, labelAngle: isMonthCombined ? 0 : -45 }, sort: { field: "order" } },
          y: { field: "value", type: "quantitative", axis: { title: "Count" } },
          color: {
            field: "source",
            type: "nominal",
            scale: { domain: ['Self-Booking', 'Staff-Booking'], range: ['#4285F4', '#EA4335'] },
          },
          tooltip: [
            { field: xField, title: xTitle },
            { field: "source", title: legendTitle },
            { field: "value", title: "Count", format: "," }
          ]
        }
      };
    }
  };

  return (
    <div id="card-mc-compare-appointment-self-and-staff">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        {/* Left: View Mode Toggles */}
        <Button.Group>
          <Button
            active={viewMode === 'overall'}
            style={viewMode === 'overall' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('overall')}
          >
            จำนวนผู้นัดหมายผ่าน Self-Booking / Staff-Booking
          </Button>
          <Button
            active={viewMode === 'age'}
            style={viewMode === 'age' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('age')}
          >
            จำนวนผู้นัดหมายผ่าน Self-Booking / Staff-Booking ตามช่วงอายุ
          </Button>
        </Button.Group>

        {/* Right: Checkbox & Chart Type */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {/* Checkbox for Combined/Separate Month View - Only visible in Overall mode */}
          {viewMode === 'overall' && (
            <Checkbox
              toggle
              label={isMonthCombined ? "รวมเดือน (Combined)" : "แยกเดือน (Separate)"}
              checked={isMonthCombined}
              onChange={(e, data) => setIsMonthCombined(data.checked || false)}
            />
          )}

          <Button.Group>
            <Button
              active={chartType === 'bar'}
              style={chartType === 'bar' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setChartType('bar')}
              icon
            >
              <Icon name='chart bar' /> Bar
            </Button>
            <Button
              active={chartType === 'line'}
              style={chartType === 'line' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setChartType('line')}
              icon
            >
              <Icon name='chart line' /> Line
            </Button>
          </Button.Group>
        </div>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          year={year}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={onSearch}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
            size='small'
            icon
            labelPosition='left'
            onClick={() => setIsTransposed(!isTransposed)}
            disabled={chartType === 'line'}
            title="Transpose"
          >
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        <CardMCVegaChart spec={getSpec()} actions={false} />
      </div>
    </div>
  );
};

export default CardMCCompareAppointmentSelfAndStaff;
